<template>
  <Page>
    <CreateScheduleFromTemplateModal
      v-if="isCreateScheduleShowing"
      :scheduleDate="scheduleFullDate"
      :onClose="onCloseCreateScheduleFromTemplate"
      :onLoadSchedule="loadSchedule" />
    <ManageScheduleItemModal
      v-if="isScheduleItemShowig"
      :scheduleDate="scheduleFullDate"
      :scheduleItem="getScheduleItem()"
      :createScheduleType="createScheduleType"
      :onClose="onCloseScheduleItem"
      :onUpdated="onUpdatedSchedule" />
    <ConfirmModal
      v-if="isDeleteScheduleModalShowing"
      :onClose="onCloseDeleteScheduleModal"
      :onConfirm="onDeleteSchedule">
      <template v-slot:header-title>
        <h4>ยืนยันการลบ</h4>
      </template>
      <div>คุณยืนยันที่จะลบผังรายการ {{ getScheduleTitle()}} </div>
    </ConfirmModal>
    <template v-slot:page-header>
      <div class="container">
        <div class="page-header">
          <div class="title-container">
            <div class="subtitle">ผังรายการ</div>
            <div class="title">{{getScheduleTitle()}}</div>
          </div>
          <div class="actions">
            <router-link
              class="btn btn-link"
              :to="'/schedule-templates'">
              จัดการเทมเพลตผังรายการ
            </router-link>
            <small
              class="text-danger mr-3"
              v-if="isServerError()">
              พบข้อผิดพลาดในการบันทึกข้อมูล กรุณาลองอีกครั้งหรือติดต่อผู้ดูแลระบบ code : {{errorStatus}}
            </small>
            <small
              class="text-success font-weight-bold mr-3"
              v-if="saveScheduleState.isSaved">
              <i class="fa fa-check" /> บันทึกแล้ว
            </small>
            <button
              type="submit"
              class="btn btn-primary"
              :disabled="!isValid"
              @click.stop="onSubmit">
              บันทึก
            </button>
            <Dropdown :alignment="'right'">
              <template v-slot:dropdow-button>
                <button
                  type="button"
                  class="btn btn-outline-dark btn-action m-0 ml-2">
                  <i class="fas fa-cog"></i>
                </button>
              </template>
              <div
                class="dropdown-item"
                @click.stop="onToggleDeleteScheduleModal">
                ลบผังรายการ
              </div>
            </Dropdown>
          </div>
        </div>
      </div>
    </template>
    <div class="container">
      <div class="row">
        <div class="col-md-4">
          <ScheduleCalendar :scheduleDate="scheduleFullDate" />
        </div>
        <div class="col-md-8">
          <PreloaderSection
            v-if="loadSchedulesState.isLoading" />
          <div
            v-if="!loadSchedulesState.isLoading"
            class="section-container">
            <div v-if="!loadSchedulesState.error">
              <Section v-if="!isEmpty()">
                <template v-slot:section-header>
                  <h1 class="title">รายการ</h1>
                  <div class="action">
                    <Dropdown :alignment="'right'">
                      <template v-slot:dropdow-button>
                        <button
                          type="button"
                          class="btn btn-light">
                          สร้าง
                        </button>
                      </template>
                      <div
                        class="dropdown-item"
                        @click.stop="onCreateScheduleItem('newSchedule')">
                        สร้างใหม่
                      </div>
                      <div
                        class="dropdown-item"
                        @click.stop="onCreateScheduleItem('existingProgram')">
                        เลือกจากรายการและตอนที่มีอยู่แล้ว
                      </div>
                    </Dropdown>
                  </div>
                </template>
                <template v-slot:section-body>
                  <List v-if="!isEmptyItems()">
                    <template v-slot:list-header>
                      <div class="list-item-image" />
                      <div class="list-item-detail">
                        <div class="list-item list-item-option">
                          เวลาออนแอร์
                        </div>
                        <div class="list-item list-item-primary list-item-title">
                          ชื่อรายการ
                        </div>
                        <div class="list-item list-item-action"></div>
                      </div>
                    </template>
                    <template v-slot:list-body>
                      <div class="list-item-container no-underline"
                        v-for="(item, itemIndex) in schedule.items"
                        :key="itemIndex">
                        <div class="list-item-image">
                          <ImageView
                            :source="item.coverImage" />
                        </div>
                        <div class="list-item-detail">
                          <div class="list-item list-item-option">
                            {{formatTime(item.startTime)}} - {{formatTime(item.endTime)}}
                          </div>
                          <div class="list-item list-item-primary list-item-title">
                            <div class="title">{{item.programTitle}}</div>
                            <div class="subtitle" v-if="item.episodeTitle">
                              {{ item.episodeTitle | truncate(48, '...')}}
                            </div>
                            <div class="subtitle tagline" v-if="item.taglineCustom">
                              {{ item.taglineCustom | truncate(48, '...')}}
                            </div>
                            <div class="schedule-option-container">
                              <ScheduleItemOption
                                :isRerun="item.isRerun"
                                :features="item.features"
                                :broadcastingChannels="item.broadcastingChannels" />
                            </div>
                          </div>
                          <div class="list-item list-item-action">
                            <div class="action-container">
                              <button
                                class="btn btn-light btn-action"
                                @click="onRemove(itemIndex)">
                                <i class="far fa-trash-alt"></i>
                              </button>
                              <button
                                class="btn btn-light btn-action"
                                @click="onSelect(itemIndex)">
                                <i class="fas fa-pencil-alt"></i>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </template>
                  </List>
                  <EmptyMessage v-if="isEmptyItems()">
                    <h5 class="title">ยังไม่มีรายการ</h5>
                  </EmptyMessage>
                </template>
              </Section>
              <Section v-if="isEmpty()">
                <EmptyMessage>
                  <h5 class="title">ยังไม่มีผังรายการวันนี้</h5>
                  <button
                    type="button"
                    class="btn btn-outline-dark btn-lg mt-4"
                    @click.stop="onCreateScheduleFromTemplate">
                    สร้างผังรายการ
                  </button>
                </EmptyMessage>
              </Section>
            </div>
            <div v-if="loadSchedulesState.error">
              <EmptyMessage>
                <h3>พบข้อผิดพลาดระหว่างโหลดข้อมูล</h3>
                <p>
                  โปรดรอซักครู่เพื่อโหลดข้อมูลใหม่ หรือติดต่อผู้ดูแลระบบ
                </p>
                <span
                  slot="code"
                  class="code">
                  Code : {{errorStatus || 'not found' }}
                </span>
              </EmptyMessage>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Page>
</template>

<script>
import Vue from 'vue';
import _ from 'lodash';
import dateFormetter from '@/utils/dateFormatter';
import scheduleManagement from '@/utils/scheduleManagement';
import Page from '@/layouts/Page.vue';
import Section from '@/components/Section.vue';
import List from '@/components/List.vue';
import Dropdown from '@/components/Dropdown.vue';
import EmptyMessage from '@/components/EmptyMessage.vue';
import ConfirmModal from '@/components/ConfirmModal.vue';
import PreloaderSection from '@/components/PreloaderSection.vue';
import ImageView from '@/image/ImageView.vue';

import {
  loadDailySchedulesByDate,
  updateDailySchedulesById,
  deleteDailySchedulesById,
} from './api';
import ManageScheduleItemModal from './ManageScheduleItemModal.vue';
import CreateScheduleFromTemplateModal from './CreateScheduleFromTemplateModal.vue';
import ScheduleCalendar from './ScheduleCalendar.vue';
import ScheduleItemOption from './scheduleItem/ScheduleItemOption.vue';

export default {
  mixins: [dateFormetter, scheduleManagement],
  components: {
    ImageView,
    Section,
    List,
    Dropdown,
    EmptyMessage,
    ScheduleItemOption,
    ConfirmModal,
    PreloaderSection,
    Page,
    ManageScheduleItemModal,
    CreateScheduleFromTemplateModal,
    ScheduleCalendar,
  },
  data() {
    return {
      schedule: {
        items: [],
      },
      selectedScheduleItemIndex: null,
      createScheduleType: 'newSchedule', // existingProgram
      isScheduleItemShowig: false,
      isCreateScheduleShowing: false,
      isDeleteScheduleModalShowing: false,
      loadSchedulesState: {
        isLoading: false,
        error: null,
      },
      errorStatus: null,
      saveScheduleState: {
        isSaving: false,
        isSaved: false,
        error: {
          name: null,
          tagline: null,
        },
      },
    };
  },
  computed: {
    scheduleYear() {
      return this.$route.params.scheduleYear;
    },
    scheduleMonth() {
      return this.$route.params.scheduleMonth;
    },
    scheduleDate() {
      return this.$route.params.scheduleDate;
    },
    scheduleFullDate() {
      return this.transformParamsToDate(this.$route.params);
    },
    isValid() {
      return true;
    },
  },
  watch: {
    scheduleYear() {
      this.load();
    },
    scheduleMonth() {
      this.load();
    },
    scheduleDate() {
      this.load();
    },
  },
  created() {
    this.load();
  },
  methods: {
    isEmptyScheduleParams() {
      return !this.scheduleYear || !this.scheduleMonth || !this.scheduleDate;
    },
    isEmpty() {
      return _.isEmpty(this.schedule);
    },
    isEmptyItems() {
      return this.schedule && _.isEmpty(this.schedule.items);
    },
    load() {
      if (this.isEmptyScheduleParams()) {
        const today = this.getToday();
        this.$router.push(
          `/schedules/${today.year}/${today.month}/${today.date}`,
        );
      } else {
        this.loadSchedule(
          this.scheduleYear,
          this.scheduleMonth,
          this.scheduleDate,
        );
      }
    },
    async loadSchedule(year, month, date) {
      this.loadSchedulesState.isLoading = true;
      try {
        const response = await loadDailySchedulesByDate(year, month, date);
        this.schedule = response.data;
        this.loadSchedulesState.isLoading = false;
        this.loadSchedulesState.error = null;
        this.onCloseCreateScheduleFromTemplate();
        this.onClearState();
      } catch (error) {
        this.loadSchedulesState.error = error.response.data;
        this.loadSchedulesState.isLoading = false;
      }
    },
    onClearState() {
      this.saveScheduleState = {
        isSaving: false,
        isSaved: false,
        error: {
          name: null,
          tagline: null,
        },
      };
    },
    sortScheduleItemsByStartTime(scheduleItems) {
      return scheduleItems.sort((firstEl, secondEl) => {
        const a = this.convertTimeStringToDate(firstEl);
        const b = this.convertTimeStringToDate(secondEl);

        if (a.startTime.getTime() < b.startTime.getTime()) {
          return -1;
        }
        if (a.startTime.getTime() > b.startTime.getTime()) {
          return 1;
        }
        return a.endTime.getTime() - b.endTime.getTime();
      });
    },
    convertTimeStringToDate(item) {
      return {
        startTime: new Date(item.startTime),
        endTime: new Date(item.endTime),
      };
    },
    isServerError() {
      return this.errorStatus && this.errorStatus !== 422;
    },
    async onSubmit() {
      this.saveScheduleState.isSaving = true;
      this.saveScheduleState.isSaved = false;
      try {
        const response = await updateDailySchedulesById(
          this.schedule.id,
          this.schedule,
        );
        this.schedule = response.data;
        this.schedule.items = this.sortScheduleItemsByStartTime(this.schedule.items);
        this.saveScheduleState.isSaving = false;
        this.saveScheduleState.isSaved = true;
      } catch (error) {
        this.saveScheduleState.error = error.response.data;
        this.errorStatus = error.response.status;
        this.saveScheduleState.isSaving = true;
        this.saveScheduleState.isSaved = true;
      }
    },
    async onDeleteSchedule() {
      await deleteDailySchedulesById(this.schedule.id);
      this.onCloseDeleteScheduleModal();
      this.load();
    },
    getScheduleTitle() {
      const date = _.get(this.schedule, 'date', null);
      if (date) {
        return this.formatDate(date);
      }
      return `${this.scheduleDate}/${this.scheduleMonth}/${this.scheduleYear}`;
    },
    onUpdatedSchedule(scheduleItem) {
      if (!_.isNull(this.selectedScheduleItemIndex)) {
        Vue.set(
          this.schedule.items,
          this.selectedScheduleItemIndex,
          scheduleItem,
        );
      } else {
        this.schedule.items = [_.cloneDeep(scheduleItem), ...this.schedule.items];
      }

      // re-sort
      this.schedule.items = this.sortScheduleItemsByStartTime(this.schedule.items);

      // on close modal
      this.onCloseScheduleItem();
    },
    onRemove(itemIndex) {
      this.schedule.items = _.remove(this.schedule.items, (item, index) => index !== itemIndex);
    },
    onCloseScheduleItem() {
      this.selectedScheduleItemIndex = null;
      this.createScheduleType = 'newSchedule';
      this.isScheduleItemShowig = false;
    },
    onToggleScheduleItem() {
      this.isScheduleItemShowig = !this.isScheduleItemShowig;
    },
    getScheduleItem() {
      const scheduleItem = _.get(this.schedule.items, this.selectedScheduleItemIndex, null);
      if (!scheduleItem) {
        return {};
      }
      return scheduleItem;
    },
    onCreateScheduleItem(creatType) {
      this.createScheduleType = creatType;
      this.onToggleScheduleItem();
    },
    onSelect(itemIndex) {
      this.selectedScheduleItemIndex = itemIndex;
      this.onToggleScheduleItem();
    },
    onCloseCreateScheduleFromTemplate() {
      this.isCreateScheduleShowing = false;
    },
    onToggleCreateScheduleFromTemplate() {
      this.isCreateScheduleShowing = !this.isCreateScheduleShowing;
    },
    onCreateScheduleFromTemplate() {
      this.onToggleCreateScheduleFromTemplate();
    },
    onToggleDeleteScheduleModal() {
      this.isDeleteScheduleModalShowing = !this.isDeleteScheduleModalShowing;
    },
    onCloseDeleteScheduleModal() {
      this.isDeleteScheduleModalShowing = false;
    },
  },
};
</script>

<style lang="scss" scoped>
@import './src/assets/scss/variables.scss';
@import './src/assets/scss/themes/components/form.scss';
@import './src/assets/scss/themes/components/section-container.scss';
</style>
